import { useReactTable } from "@tanstack/react-table";
import React, { useMemo } from "react";
import type { Transaction } from "../../../Contexts/TransactionsContext";
import { Button, Input, Select } from "@aidkitorg/component-library";
import { SelectOptionType } from "@aidkitorg/component-library/dist/types/src/Select";
import { useLocalizedStrings } from "../../../Localization";
import { useTransactionsContext } from "../../../Contexts/TransactionsContext";
import { paymentTypeToString } from "../../../utils/payment_utils";
import type { Transactions } from '@aidkitorg/types/lib/survey';

export type TransactionsFilterProps = {
  table: ReturnType<typeof useReactTable<Transaction>>
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>
  filters: Filters,
  setFilters: React.Dispatch<React.SetStateAction<Filters>>
  onApplyFilters: (startDate: string, endDate: string) => void
  defaultStartDate: string,
  defaultEndDate: string
  pageConfig?: Transactions
}

export type Filters = {
  transactionStart: string,
  transactionEnd: string,
  rcStatus: string,
  transactionState: string,
  entryType: string,
  paymentProvider: string,
}

export const TransactionsFilter: React.FC<TransactionsFilterProps> = ({
  table,
  setActiveFilters,
  filters,
  setFilters,
  onApplyFilters,
  defaultStartDate,
  defaultEndDate,
  pageConfig
}) => {
  const L = useLocalizedStrings()
  const { paymentProviderOptions: fetchedPaymentProviderOptions } = useTransactionsContext()
  const paymentProviderOptions: Array<SelectOptionType> = useMemo(() => {
    const toReturn = fetchedPaymentProviderOptions.map(v => ({ value: v, label: paymentTypeToString(v) }));
    toReturn.unshift({ value: "", label: L.admin_panel.transactions.all_providers });
    return toReturn;
  }, [fetchedPaymentProviderOptions, L]);

  // Determine which filters to show based on pageConfig
  const showTransactionDateFilter = pageConfig?.columns?.transactionDate === 'visibleAndFilterable';
  const showRcStatusFilter = pageConfig?.columns?.RCStatus === 'visibleAndFilterable';
  const showTransactionStateFilter = pageConfig?.columns?.transactionState === 'visibleAndFilterable';
  const showEntryTypeFilter = pageConfig?.columns?.entryType === 'visibleAndFilterable';
  const showPaymentProviderFilter = pageConfig?.columns?.paymentProvider === 'visibleAndFilterable';

  const applyFilters = () => {
    let activeFilters = 0;

    // Fetch transactions with the date range
    if (filters.transactionStart && filters.transactionEnd) {
      onApplyFilters(filters.transactionStart, filters.transactionEnd)
    }

    const transactionDateCol = table.getColumn("transaction_clear_date")
    if (filters.transactionStart !== "" || filters.transactionEnd !== "") activeFilters++
    transactionDateCol?.setFilterValue(
      filters.transactionStart || filters.transactionEnd
        ? { transactionStart: filters.transactionStart, transactionEnd: filters.transactionEnd }
        : undefined
    )

    const rcStatusCol = table.getColumn("rc_status")
    if (filters.rcStatus !== "") activeFilters++
    rcStatusCol?.setFilterValue(filters.rcStatus || undefined)

    const transactionStateCol = table.getColumn("transaction_state")
    if (filters.transactionState !== "") activeFilters++
    transactionStateCol?.setFilterValue(filters.transactionState || undefined)

    const entryTypeCol = table.getColumn("entry_type")
    if (filters.entryType !== "") activeFilters++
    entryTypeCol?.setFilterValue(filters.entryType || undefined)

    const paymentProviderCol = table.getColumn("payment_provider")
    if (filters.paymentProvider !== "") activeFilters++
    paymentProviderCol?.setFilterValue(filters.paymentProvider || undefined)

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      transactionStart: defaultStartDate,
      transactionEnd: defaultEndDate,
      rcStatus: "",
      transactionState: "",
      entryType: "",
      paymentProvider: "",
    })
    setActiveFilters(0)
    table.resetColumnFilters()
    // Re-fetch with default dates when resetting
    onApplyFilters(defaultStartDate, defaultEndDate)
  }

  const RC_STATUS_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.payments.all_statuses,
    },
    {
      value: "PROPOSED",
      label: L.admin_panel.transactions.rc_status_proposed,
    },
    {
      value: "ANTICIPATED",
      label: L.admin_panel.transactions.rc_status_anticipated,
    },
    {
      value: "PENDING",
      label: L.admin_panel.transactions.rc_status_pending,
    },
    {
      value: "SCHEDULED",
      label: L.admin_panel.transactions.rc_status_scheduled,
    },
    {
      value: "FAILED",
      label: L.admin_panel.transactions.rc_status_failed,
    },
    {
      value: "CANCELLED",
      label: L.admin_panel.transactions.rc_status_cancelled,
    },
    {
      value: "COMPLETE",
      label: L.admin_panel.transactions.rc_status_complete,
    }
  ]

  const TRANSACTION_STATE_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.payments.all_statuses,
    },
    {
      value: "pending",
      label: L.admin_panel.transactions.transaction_state_pending,
    },
    {
      value: "cleared",
      label: L.admin_panel.transactions.transaction_state_cleared,
    },
    {
      value: "failed",
      label: L.admin_panel.transactions.transaction_state_failed,
    },
    {
      value: "cancelled",
      label: L.admin_panel.transactions.transaction_state_cancelled,
    }
  ]

  const ENTRY_TYPE_FILTER_VALUES: Array<SelectOptionType> = [
    {
      value: "",
      label: L.admin_panel.transactions.entry_type_all,
    },
    {
      value: "grant_out",
      label: L.admin_panel.transactions.entry_type_grant_out,
    },
    {
      value: "grant_source",
      label: L.admin_panel.transactions.entry_type_grant_source,
    },
    {
      value: "return_in",
      label: L.admin_panel.transactions.entry_type_return_in,
    }
  ]

  const getRcStatusOption = (value: string) => (RC_STATUS_FILTER_VALUES.find((v) => v.value === value))
  const getTransactionStateOption = (value: string) => (TRANSACTION_STATE_FILTER_VALUES.find((v) => v.value === value))
  const getEntryTypeOption = (value: string) => (ENTRY_TYPE_FILTER_VALUES.find((v) => v.value === value))
  const getPaymentProviderOption = (value: string) => (paymentProviderOptions.find((v) => v.value === value))

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        {showTransactionDateFilter && (
          <div>
            <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.transactions.transaction_date}</label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Transaction date from"
                type="date"
                value={filters.transactionStart}
                onChange={(e) => setFilters(f => ({ ...f, transactionStart: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
              <span className="text-gray-700">{L.admin_panel.transactions.date_range_to}</span>
              <input
                aria-label="Transaction date to"
                type="date"
                value={filters.transactionEnd}
                onChange={(e) => setFilters(f => ({ ...f, transactionEnd: e.target.value }))}
                className="h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
              />
            </div>
          </div>
        )}

        {showRcStatusFilter && (
          <div>
            <Select
              className="h-10"
              options={RC_STATUS_FILTER_VALUES}
              description={L.admin_panel.transactions.rc_status_desc}
              label={L.admin_panel.transactions.rc_status}
              labelLocation="above"
              value={getRcStatusOption(filters.rcStatus)}
              onChange={(value) => setFilters(f => ({... f, rcStatus: value.value}))}
            />
          </div>
        )}

        {showTransactionStateFilter && (
          <div>
            <Select
              className="h-10"
              options={TRANSACTION_STATE_FILTER_VALUES}
              description={L.admin_panel.transactions.transaction_state_desc}
              label={L.admin_panel.transactions.transaction_state}
              labelLocation="above"
              value={getTransactionStateOption(filters.transactionState)}
              onChange={(value) => setFilters(f => ({... f, transactionState: value.value}))}
            />
          </div>
        )}

        {showEntryTypeFilter && (
          <div>
            <Select
              className="h-10"
              options={ENTRY_TYPE_FILTER_VALUES}
              description={L.admin_panel.transactions.entry_type_desc}
              label={L.admin_panel.transactions.entry_type}
              labelLocation="above"
              value={getEntryTypeOption(filters.entryType)}
              onChange={(value) => setFilters(f => ({... f, entryType: value.value}))}
            />
          </div>
        )}

        {showPaymentProviderFilter && (
          <div>
            <Select
              className="h-10"
              options={paymentProviderOptions}
              description={L.admin_panel.transactions.payment_provider_desc}
              label={L.admin_panel.transactions.payment_provider}
              labelLocation="above"
              value={paymentProviderOptions.find((v) => v.value === filters.paymentProvider)}
              onChange={(value) => setFilters(f => ({... f, paymentProvider: value.value}))}
            />
          </div>
        )}
      </div>
      <div className="pt-4">
        <div className="flex gap-2">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.payments.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.payments.apply_filters}</Button>
        </div>
      </div>
    </div>
  )
}

